import { Controller } from "@hotwired/stimulus"

const DEBOUNCE_MS = 200

export default class extends Controller {
  static targets = ["input", "results"]
  static values = { url: String }

  connect() {
    this._debounceTimer = null
    this._abortController = null
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    if (this._abortController) this._abortController.abort()
  }

  onInput() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this.fetchResults(), DEBOUNCE_MS)
  }

  async fetchResults() {
    const q = this.inputTarget.value.trim()
    if (q.length < 2) {
      this.hideResults()
      return
    }
    if (this._abortController) this._abortController.abort()
    this._abortController = new AbortController()
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(q)}`
      const resp = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this._abortController.signal
      })
      if (!resp.ok) { this.hideResults(); return }
      const data = await resp.json()
      this.renderResults(data.results || [])
    } catch (err) {
      if (err.name !== "AbortError") this.hideResults()
    }
  }

  renderResults(results) {
    while (this.resultsTarget.firstChild) {
      this.resultsTarget.removeChild(this.resultsTarget.firstChild)
    }
    if (!results.length) {
      this.hideResults()
      return
    }
    for (const r of results) {
      this.resultsTarget.appendChild(this.buildResultItem(r))
    }
    this.resultsTarget.classList.remove("hidden")
  }

  buildResultItem(result) {
    const li = document.createElement("li")
    li.setAttribute("data-tree-search-target", "result")
    li.setAttribute("data-person-id", result.personId)
    li.setAttribute("data-node-key", result.nodeKey)
    li.setAttribute("role", "option")
    li.setAttribute("tabindex", "0")
    li.className = "px-3 py-2 text-sm cursor-pointer hover:bg-glass-2 rounded-md"

    const name = document.createElement("div")
    name.className = "font-medium text-primary"
    name.textContent = result.displayName
    li.appendChild(name)

    if (result.villageOfOrigin) {
      const meta = document.createElement("div")
      meta.className = "text-xs text-muted"
      meta.textContent = result.villageOfOrigin
      li.appendChild(meta)
    }

    li.addEventListener("click", () => this.selectResult(result))
    li.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        this.selectResult(result)
      }
    })
    return li
  }

  selectResult(result) {
    this.hideResults()
    this.inputTarget.value = ""
    this.dispatch("zoomToPerson", { detail: { nodeKey: result.nodeKey, personId: result.personId } })
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
    while (this.resultsTarget.firstChild) {
      this.resultsTarget.removeChild(this.resultsTarget.firstChild)
    }
  }
}
